import React from "react";
import PropTypes from "prop-types";
import GridCell from "./GridCell";

/**
 * NormalGrid renders a standard row/column grid layout.
 * @param {object} props - Component props.
 * @param {Array} props.rows - Row config array from gridConfig.
 * @param {object} props.gridConfig - Grid configuration (gaps, columns, etc.).
 * @param {Map} props.contentMap - Map of cell keys to content data.
 * @param {object} props.videoRefs - Ref map to video DOM elements.
 * @param {Function} props.onToggleVideoPlay - Callback to toggle video play state.
 * @param {object} props.gridTextStyle - Text style settings for grid cells.
 * @param {object} props.gridBorder - Border style settings for grid cells.
 * @param {boolean} props.isAboveFold - Whether this grid is expected above the fold.
 * @param {boolean} props.isLcpCandidate - Whether this grid may contain the LCP image.
 * @returns {React.ReactElement} The normal grid layout.
 */
const NormalGrid = ({
  rows,
  gridConfig,
  contentMap,
  videoRefs,
  onToggleVideoPlay,
  gridTextStyle,
  gridBorder,
  isMobile,
  isAboveFold,
  isLcpCandidate,
}) => {
  /**
   * Returns a stable key for a cell object.
   * @param {object} cell - The cell object.
   * @param {string} fallback - Fallback key string.
   * @returns {string} The cell key.
   */
  const getCellKey = (cell, fallback) => cell.cellId || cell.id || fallback;

  /**
   * Merges cell config with content map data.
   * @param {object} cell - The raw cell config.
   * @param {string} fallbackKey - Fallback key if cell has no id.
   * @returns {object} Merged cell object.
   */
  const mergeCell = (cell, fallbackKey) => {
    const key = getCellKey(cell, fallbackKey);
    const fromContent = contentMap.get(key);
    return fromContent ? { ...cell, ...fromContent } : cell;
  };

  const borderRadius = gridBorder?.borderRadius ?? 0;
  const borderThickness = gridBorder?.borderThickness ?? 0;
  const borderColor = gridBorder?.borderColor || "transparent";
  const borderStyle =
    borderThickness > 0 ? `${borderThickness}px solid ${borderColor}` : isMobile ? undefined : "2px solid transparent";

  return (
    <div id="dynamic_grid-normal_grid" className="w-full" style={{ rowGap: gridConfig.rowGap ?? 0 }}>
      {rows.map((row, rowIndex) => {
        const columns = row.columns ?? 1;
        const gap = gridConfig.columnGap ?? row.gap ?? 0;

        return (
          <div
            key={row.id || `row-${rowIndex}`}
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap,
              marginBottom: rows?.length > 1 && rowIndex < rows.length - 1 ? (gridConfig.rowGap ?? 0) : 0,
            }}
          >
            {row.cells.map((rawCell, cellIndex) => {
              const cell = mergeCell(rawCell, `${row.id}-${cellIndex}`);
              const colSpan = cell.colSpan ?? 1;
              const rowSpan = cell.rowSpan ?? 1;
              const ratio = cell.aspectRatio ?? row.aspectRatio;

              return (
                <div
                  key={getCellKey(cell, `${row.id}-${cellIndex}`)}
                  style={{
                    gridColumn: `span ${colSpan} / span ${colSpan}`,
                    gridRow: `span ${rowSpan} / span ${rowSpan}`,
                  }}
                >
                  <GridCell
                    cell={cell}
                    index={cellIndex}
                    onVideoToggle={onToggleVideoPlay}
                    videoRefs={videoRefs}
                    gridTextStyle={gridTextStyle}
                    columnCount={columns}
                    isMobile={isMobile}
                    aspectRatio={ratio}
                    cellBorderRadius={cell.borderRadius ?? borderRadius}
                    cellBorderStyle={borderStyle}
                    cellBorderThickness={borderThickness}
                    isEager={isAboveFold}
                    isPriority={isLcpCandidate}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

NormalGrid.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  gridConfig: PropTypes.object.isRequired,
  contentMap: PropTypes.instanceOf(Map).isRequired,
  videoRefs: PropTypes.object.isRequired,
  onToggleVideoPlay: PropTypes.func.isRequired,
  gridTextStyle: PropTypes.object,
  gridBorder: PropTypes.shape({
    borderRadius: PropTypes.number,
    borderThickness: PropTypes.number,
    borderColor: PropTypes.string,
  }),
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default NormalGrid;
